import { useAuth } from "@/src/contexts/auth.context";
import { usePostCard } from "@/src/hooks/features/posts/use-post-card";
import { usePermissions } from "@/src/hooks/use-permissions";
import { Post } from "@/src/models/post.model";
import { useSendNotification } from "@/src/services/api/notification/notification.hooks";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Image,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { useTheme } from "@/src/styles/theme-provider";
import ShareModal from "../action-modals/share-modal";
import PostDetailModal from "./post-detail-modal";
import { FileAttachment } from "./post/file-attachment";
import PostActions from "./post/post-actions";
import PostHeader from "./post/post-header";
import VideoPreview from "./videos/video-preview";
import TagPill from "./tag-pill";
import MentionedUserPill from "./mentioned-user-pill";
dayjs.extend(relativeTime);

type PostProps = Readonly<{
  post: Post;
  onEdit?: (post: Post) => void;
  onDelete?: (post: Post) => void;
  onReactorCount?: () => void;
}>;

const PostCard = ({ post, onEdit, onDelete, onReactorCount }: PostProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { themeVars } = useTheme();
  const { mutate: sendNotification } = useSendNotification();
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 768;

  const [cardWidth, setCardWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(300);

  const postCard = usePostCard(post, { onEdit, onDelete });

  const { canUpdate, canDelete } = usePermissions();
  const isOwner = post?.CreatedBy === user?.itemId;

  const handleShareNotification = () => {
    if (post.authorSnapshot?.userId === user?.itemId) return;
    sendNotification({
      userIds: [post.authorSnapshot.userId],
      denormalizedPayload: JSON.stringify({
        type: "post_share",
        message: `${user?.firstName} ${user?.lastName} shared your post`,
        actorAvatar: user?.profileImageUrl,
        targetEntityId: post.ItemId,
      }),
      configuratoinName: "post-share",
      responseKey: "post-action",
      responseValue: "share",
    });
  };
  const [imageAspectRatio, setImageAspectRatio] = useState(1);
  return (
    <View className="relative">
      {postCard.menuOpen && (
        <Pressable
          className="fixed inset-0 z-40"
          onPress={() => postCard.setMenuOpen(false)}
        />
      )}
      <View
        className="bg-chalk overflow-hidden rounded-xl md:rounded-2xl md:border md:border-ash-3"
        onLayout={(e) => setCardWidth(e.nativeEvent.layout.width)}
      >
        <PostHeader
          post={post}
          menuItems={[
            ...(isOwner && canUpdate("post")
              ? [{ label: t("LABEL_EDIT"), onPress: postCard.handleEdit }]
              : []),
            ...(isOwner && canDelete("post")
              ? [
                {
                  label: t("LABEL_DELETE"),
                  onPress: postCard.handleDelete,
                  destructive: true,
                },
              ]
              : []),
            {
              label: postCard.isSaved ? t("LABEL_UNSAVE") : t("LABEL_SAVE"),
              onPress: postCard.handleToggleSave,
            },
            { label: t("LABEL_REPORT"), onPress: () => { } },
          ]}
        />

        <View>
          <View className="px-4">
            {!postCard.expanded && (
              <Text
                className="text-sm leading-5 absolute opacity-0 z-[-1] text-onyx pt-2"
                onLayout={(e) =>
                  postCard.setFullHeight(e.nativeEvent.layout.height)
                }
              >
                {post.content}
              </Text>
            )}

            {post.content && (
              <Text
                className="text-sm leading-5 text-onyx pt-5 py-5"
                numberOfLines={
                  postCard.expanded ? undefined : postCard.MAX_LINES
                }
                ellipsizeMode="tail"
                onLayout={(e) => {
                  if (!postCard.expanded)
                    postCard.setLimitedHeight(e.nativeEvent.layout.height);
                }}
              >
                {post.content}
              </Text>
            )}

            {postCard.showSeeMore && (
              <Pressable
                onPress={() => postCard.setExpanded((prev) => !prev)}
                className="hover:bg-ash-3 hover:rounded-lg"
              >
                <Text className="text-primary text-xs font-medium mt-1">
                  {postCard.expanded
                    ? t("LABEL_SEE_LESS")
                    : t("LABEL_SEE_MORE")}
                </Text>
              </Pressable>
            )}
            {(post.hashtags ?? []).length > 0 && (
              <View className="flex-row flex-wrap justify-start w-full">
                {(post.hashtags ?? []).map((tag) => (
                  <TagPill key={tag} tag={tag} onRemove={() => { }} isEditing={false} />
                ))}
              </View>
            )}

            {(post.mentionedUsers ?? []).length > 0 && (
              <View className="flex-row flex-wrap w-full">
                {(post.mentionedUsers ?? []).map((user) => (
                  <MentionedUserPill
                    key={user.itemId}
                    displayName={[user.firstName, user.lastName].filter(Boolean).join(" ") || "Unknown"}
                    userId={user.userId}
                    isEditing={false}
                  />
                ))}
              </View>
            )}
          </View>

          {postCard.otherFiles.length > 0 && (
            <View className="px-4 pt-2">
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 8, paddingBottom: 4 }}
              >
                {postCard.otherFiles.map((url) => (
                  <FileAttachment key={url} fileUrl={url} />
                ))}
              </ScrollView>
            </View>
          )}

          {postCard.firstMedia && cardWidth > 0 && (
            <Pressable
              onPress={() => postCard.setViewerOpen(true)}
              className={`active:opacity-90 items-center ${post.content ? "mt-2" : ""}`}
              style={{
                backgroundColor: isMobile
                  ? (themeVars["--color-chalk-default"] as string)
                  : postCard.imageBgColor,
              }}
            >
              {postCard.isFirstMediaVideo ? (
                <View
                  pointerEvents="none"
                  style={{
                    width: "100%",
                    height: 350,
                    overflow: "hidden",
                    backgroundColor: themeVars["--color-ash-default"] as string,
                  }}
                >
                  <VideoPreview uri={postCard.firstMedia} height={350} />
                </View>
              ) : (
                <View
                  style={{
                    width: "100%",
                    maxHeight: isMobile ? 420 : 500,
                    aspectRatio: Math.max(imageAspectRatio, 0.5),
                  }}
                >
                  <Image
                    source={{ uri: postCard.firstMedia }}
                    style={{ width: "100%", height: "100%" }}
                    onLoad={() => {
                      Image.getSize(
                        postCard.firstMedia!,
                        (imgW, imgH) => setImageAspectRatio(imgW / imgH),
                        () => setImageAspectRatio(1),
                      );
                      postCard.handleImageLoad(postCard.firstMedia!);
                    }}
                    resizeMode="contain"
                  />
                </View>
              )}
              {postCard.totalMediaCount > 1 && (
                <View className="absolute top-3 right-3 bg-primary px-3 py-1 rounded-full">
                  <Text className="text-white text-xs">
                    1/{postCard.totalMediaCount}
                  </Text>
                </View>
              )}
            </Pressable>
          )}
        </View>

        <PostActions
          userReaction={postCard.userReaction?.type}
          onReact={postCard.handleReact}
          reactionCounts={post.reactionCount}
          reactionTotal={Object.values(post.reactionCount || {}).reduce(
            (sum, val) => sum + (val ?? 0),
            0,
          )}
          commentCount={post.commentCount}
          onComment={() => postCard.setViewerOpen(true)}
          onShare={() => postCard.setShareModalVisible(true)}
          saveCount={post.saveCount}
          onSave={postCard.handleToggleSave}
          onReactorCount={onReactorCount}
        />
      </View>

      <PostDetailModal
        visible={postCard.viewerOpen}
        postId={post.ItemId}
        initialIndex={postCard.selectedIndex}
        onClose={() => postCard.setViewerOpen(false)}
      />
      <ShareModal
        visible={postCard.shareModalVisible}
        onClose={() => postCard.setShareModalVisible(false)}
        resourceId={post.ItemId}
        resourcePath="feeds"
        onShare={handleShareNotification}
      />
    </View>
  );
};

export default PostCard;
